import * as React from 'react';
import { ThemeProviderContext, type ThemeModel } from '../../hooks/useTheme';
import { THEME_OPTIONS, type ThemeValue } from '../BasicLayout/components/ThemeSelector/constant';
import { COLLAPSED_BREAKPOINT } from '@/components/ui/sidebar';

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: ThemeValue;
  defaultThemeModel?: ThemeModel;
  storageKey?: string;
  themeModelStorageKey?: string;
};

/** 提供主题状态，并保留用户手动选择的布局 */
export function ThemeProvider({
  children,
  defaultTheme = 'default',
  defaultThemeModel = 'light',
  storageKey = 'ui-theme',
  themeModelStorageKey = 'ui-theme-model',
  ...props
}: ThemeProviderProps) {
  const userSelectedThemeRef = React.useRef(false);
  // 主题颜色方案状态
  const [theme, setTheme] = React.useState<ThemeValue>(() => {
    try {
      return (
        (localStorage.getItem(storageKey) as ThemeValue) ||
        (window.innerWidth > COLLAPSED_BREAKPOINT ? defaultTheme : 'default-scaled')
      );
    } catch {
      return defaultTheme;
    }
  });

  // 明暗模式状态
  const [themeModel, setThemeModel] = React.useState<ThemeModel>(() => {
    try {
      return (localStorage.getItem(themeModelStorageKey) as ThemeModel) || defaultThemeModel;
    } catch {
      return defaultThemeModel;
    }
  });

  // 应用主题类名到 DOM
  React.useEffect(() => {
    const root = window.document.documentElement;
    const body = window.document.body;
    // 移除所有可能的明暗模式类名
    root.classList.remove('light', 'dark');

    // 移除所有可能的主题类名
    THEME_OPTIONS.forEach((option) => {
      option.value.split('-').forEach((t) => {
        body.classList.remove(`theme-${t}`);
      });
    });

    // 应用明暗模式类名
    root.classList.add(themeModel);
    theme.split('-').forEach((t) => {
      // 应用主题颜色方案类名
      body.classList.add(`theme-${t}`);
    });
  }, [theme, themeModel]);

  React.useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${COLLAPSED_BREAKPOINT}px)`);
    /** 当前页面手动选择布局后，断点变化不再覆盖该选择 */
    const onChange = () => {
      if (userSelectedThemeRef.current) return;
      setTheme(window.innerWidth > COLLAPSED_BREAKPOINT ? 'default' : 'default-scaled');
    };
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [setTheme]);

  const value = {
    theme,
    themeModel,
    /** 保存手动选择的布局，避免当前页面的断点变化覆盖 */
    setTheme: (newTheme: ThemeValue) => {
      userSelectedThemeRef.current = true;
      try {
        localStorage.setItem(storageKey, newTheme);
      } catch {
        // localStorage 不可用时静默失败
      }
      setTheme(newTheme);
    },
    setThemeModel: (newThemeModel: ThemeModel) => {
      try {
        localStorage.setItem(themeModelStorageKey, newThemeModel);
      } catch {
        // localStorage 不可用时静默失败
      }
      setThemeModel(newThemeModel);
    },
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}
